import React, { useEffect } from "react";
import { Updater, useImmer } from "use-immer";

import { IgButton, IgTextField, IgTypography } from "@intelligems/ig-theme";
import { CreateAgencyDto } from "@intelligems/ig-types";
import { Box, Drawer, Grid } from "@mui/material";
import { usePutAgency } from "@src/api/agencies/put-agency";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";
import { useDispatch, useSelector } from "@src/store";
import { closeDrawer, getDrawer } from "@src/store/slices/drawers";

interface ContentProps {
  agency: CreateAgencyDto;
  setAgency: Updater<CreateAgencyDto>;
}
const Content = ({ agency, setAgency }: ContentProps) => {
  const putAgency = usePutAgency();
  const dispatch = useDispatch();

  return (
    <Box sx={{ borderTop: 1, borderColor: "divider" }}>
      <Grid container direction={"column"} rowSpacing={2} mt={2}>
        <Grid item>
          <IgTextField
            label={"Name"}
            value={agency.name}
            onChange={(e) =>
              setAgency((draft) => {
                draft.name = e.target.value;
                return draft;
              })
            }
          />
        </Grid>
        <Grid item>
          <IgTextField
            onChange={(e) =>
              setAgency((draft) => {
                draft.internalNotes = e.target.value;
                return draft;
              })
            }
            label={"Internal Notes"}
            value={agency?.internalNotes || ""}
            multiline={true}
            minRows={3}
          />
        </Grid>
        <Grid item textAlign="end">
          <IgButton
            loading={putAgency.isPending}
            variant={"contained"}
            disabled={!agency.name}
            onClick={() => {
              putAgency.mutate(
                {
                  body: { agency },
                  pathParams: { id: agency.id || undefined },
                  queryParams: undefined,
                },
                {
                  onSuccess: () => {
                    dispatch(closeDrawer("putAgency"));
                  },
                }
              );
            }}
          >
            Save
          </IgButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export const PutAgencyDrawer = () => {
  const [agency, updateAgency] = useImmer<CreateAgencyDto | null>(null);
  const drawer = useSelector(getDrawer("putAgency"));
  const dispatch = useDispatch();

  useEffect(() => {
    updateAgency(drawer?.agency ?? { name: "", internalNotes: "" });
  }, [drawer?.agency]);

  if (drawer === undefined) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open
      onClose={() => dispatch(closeDrawer("putAgency"))}
      sx={{
        zIndex: 1200,
        ".MuiDrawer-paper": { borderRadius: "0 !important" },
      }}
    >
      <Grid
        container
        direction="column"
        sx={{ width: 800, p: 3 }}
        rowSpacing={3}
      >
        <Grid item sx={{ marginBottom: "20px !important" }}>
          <IgTypography variant="h3">
            {drawer.mode === "create" ? "Create" : "Modify"} Agency
          </IgTypography>
        </Grid>
        {agency ? (
          <Content agency={agency} setAgency={updateAgency} />
        ) : (
          <SkeletonGroup lines={4} />
        )}
      </Grid>
    </Drawer>
  );
};
